import { createContext, FC, ReactNode, useEffect, useMemo } from "react";
import { adjustColorBrightness } from "@/helpers/color";
import { useGenerateCampaign } from "@/hooks/useGenerateCampaign";

interface CampaignProviderProps {
  children: ReactNode;
}

interface CampaignContextType {
  availablePathologies: Array<string>;
  campaignError: string | null;
  campaignLoading: boolean;
  document: string;
  hideAge: boolean;
  hideSensitiveSkin: boolean;
  isLaboratory: boolean;
  labIdentifier: string | undefined;
  labName: string;
  primaryColor: string;
  secondaryColor: string;
  token: string;
}

export const CampaignContext = createContext<CampaignContextType>({
  availablePathologies: [],
  campaignError: null,
  campaignLoading: false,
  document: "",
  hideAge: false,
  hideSensitiveSkin: false,
  isLaboratory: false,
  labIdentifier: undefined,
  labName: "",
  primaryColor: "",
  secondaryColor: "",
  token: "",
});

export const CampaignProvider: FC<CampaignProviderProps> = ({ children }) => {
  const { campaignData, isLoading, token, campaignError } =
    useGenerateCampaign();

  const labInfos = useMemo(() => {
    if (campaignData) {
      const {
        laboratory: {
          availablePathologies,
          document,
          hideAge,
          hideSensitiveSkin,
          name,
          identifier,
        },
      } = campaignData;
      return {
        availablePathologies,
        document,
        hideAge,
        hideSensitiveSkin,
        isLaboratory: true,
        name,
        identifier,
      };
    }
    return {
      availablePathologies: [],
      document: "",
      hideAge: false,
      hideSensitiveSkin: false,
      isLaboratory: false,
      name: "",
      identifier: undefined as string | undefined,
    };
  }, [campaignData]);

  const colors = useMemo(() => {
    if (campaignData) {
      const {
        laboratory: { color1, color2 },
      } = campaignData;
      return { primary: color1, secondary: color2 };
    }
    return { primary: "", secondary: "" };
  }, [campaignData]);

  useEffect(() => {
    if (colors.primary !== "" && colors.secondary !== "") {
      document.documentElement.style.setProperty(
        "--primary-color",
        colors.primary,
      );
      document.documentElement.style.setProperty(
        "--primary-color-dark",
        adjustColorBrightness(colors.primary, -10),
      );
      document.documentElement.style.setProperty(
        "--secondary-color",
        colors.secondary,
      );
    }
  }, [colors]);

  return (
    <CampaignContext.Provider
      value={{
        availablePathologies: labInfos.availablePathologies,
        campaignError,
        campaignLoading: isLoading,
        document: labInfos.document,
        hideAge: labInfos.hideAge,
        hideSensitiveSkin: labInfos.hideSensitiveSkin,
        isLaboratory: labInfos.isLaboratory,
        labIdentifier: labInfos.identifier,
        labName: labInfos.name,
        primaryColor: colors.primary,
        secondaryColor: colors.secondary,
        token,
      }}
    >
      {children}
    </CampaignContext.Provider>
  );
};
